<cs-header>
  <cs-header-title>{{title}}</cs-header-title>
  @if (showScanButton) {
    <cs-icon-button header-right icon="camera" (click)="onScanClick()" [ariaLabel]="LL.equipmentScanner.title()"></cs-icon-button>
  }
</cs-header>

<cs-content vld-progress [(vldStep)]="progressStep" [(vldTotalSteps)]="totalSteps">
  <cs-item-list vld-group class="create-mode-details">
    <!-- Details Section -->
    <cs-item-group [title]="LL.equipment.labels.details()">
      <div class="details-row">
        <cs-form-field class="equipment-type-field" [label]="LL.equipment.labels.equipmentType()">
          <cs-select [(value)]="selectedTemplateId" (selectionChange)="onTemplateSelected($event)"
            [placeholder]="LL.equipment.placeholders.selectTemplate()" [disabled]="!createMode">
            <!-- template.name is a localized equipment-template display name (already
                 routed through LL); Equipment.name itself is a shared data-model
                 field typed string, not display prose to translate here -->
            @for (template of templates; track template) {
              <cs-select-option [value]="template.id" [label]="template.name | untranslated">
                @if (template.id === suggestedTemplateId) {
                  <cs-badge color="gray">{{ LL.equipment.filters.suggested() }}</cs-badge>
                }
              </cs-select-option>
            }
          </cs-select>
        </cs-form-field>
        @if (!isStep1) {
          <cs-form-field flex-width class="name-field" [label]="LL.equipment.labels.name()">
            <cs-input [(value)]="equipment.name" [placeholder]="LL.equipment.placeholders.enterName()"
              autocomplete="off" (input)="somethingChanged()" />
          </cs-form-field>
        }
      </div>
      @if (!isStep1) {
        <cs-form-field fit-width class="units-field" [label]="LL.equipment.labels.units()">
          <!-- kg/lb are unit symbols, not translatable prose -->
          <cs-select [(value)]="equipment.weight_unit" (selectionChange)="userUnitChanged()">
            <cs-select-option value="kg" [label]="'kg' | untranslated"></cs-select-option>
            <cs-select-option value="lb" [label]="'lb' | untranslated"></cs-select-option>
          </cs-select>
        </cs-form-field>
      }
    </cs-item-group>

    <!-- Empty State (shown only in step 1 = create mode with no equipment yet) -->
    @if (isStep1) {
      <cs-empty-state iconName="barbell" [title]="LL.equipment.emptyState.chooseTemplate.title()"
        [subtext]="LL.equipment.emptyState.chooseTemplate.subtext()">
        <cs-button secondary small icon="camera" (click)="onScanClick()">
          {{ LL.equipmentScanner.title() }}
        </cs-button>
      </cs-empty-state>
    }

    <!-- Custom Increments Section (Manual type) -->
    @if (!isStep1 && equipment.type === EquipmentType.Manual) {
      <cs-item-group
        [title]="LL.equipment.sections.incrementWeights()">
        <div class="section-description">
          {{ LL.equipment.descriptions.manual() }}
        </div>
        <div class="weight-inputs-row">
          <cs-form-field class="weight-field" [label]="LL.equipment.labels.minimumWeight()">
            <cs-input-measure id="minimumWeight" type="addedweight_kg" [(userUnit)]="equipment.weight_unit"
              [(canonValue)]="equipment.weight_min_kg" (userUnitChange)="userUnitChanged()"
            (editDone)="someWeightChanged()" [showUnit]="false"></cs-input-measure>
            <vld-message [validity]="validity('weight_min')" [immediate]="true"></vld-message>
          </cs-form-field>
          <cs-form-field class="weight-field" [label]="LL.equipment.labels.weightIncrement()">
            <cs-input-measure id="weightIncrement" type="addedweight_kg" [(userUnit)]="equipment.weight_unit"
              [(canonValue)]="equipment.weight_inc_kg" (userUnitChange)="userUnitChanged()"
            (editDone)="someWeightChanged()" [showUnit]="false"></cs-input-measure>
            <vld-message [validity]="validity('weight_inc')" [immediate]="true"></vld-message>
          </cs-form-field>
        </div>
      </cs-item-group>
    }

    <!-- Barbell Weight Section (Bar type only) -->
    @if (!isStep1 && equipment.type === EquipmentType.Bar) {
      <cs-item-group
        [title]="LL.equipment.sections.barbellWeight()">
        <div class="weight-inputs-row">
          <cs-form-field class="weight-field" [label]="LL.equipment.labels.bar()">
            <cs-input-measure type="addedweight_kg" [(userUnit)]="equipment.weight_unit" [(canonValue)]="equipment.bar_kg"
            (userUnitChange)="userUnitChanged()" (editDone)="someWeightChanged()" [showUnit]="false"></cs-input-measure>
            <div class="field-hint">{{ LL.equipment.notes.barWeight() }}</div>
            <vld-message [validity]="validity('bar')" [immediate]="true"></vld-message>
          </cs-form-field>
          <cs-form-field class="weight-field" [label]="LL.equipment.labels.collarWeightOptional()">
            <cs-input-measure type="addedweight_kg" [(userUnit)]="equipment.weight_unit"
              [(canonValue)]="equipment.collar_kg" (userUnitChange)="userUnitChanged()" (editDone)="someWeightChanged()"
            [showUnit]="false"></cs-input-measure>
            @if (equipment.collar_kg > 0) {
              <div class="field-hint">
                {{ LL.equipment.notes.bothCollars({weight: collars2x_text}) }}
              </div>
            }
            @if (!(equipment.collar_kg > 0)) {
              <div class="field-hint">
                {{ LL.equipment.notes.keepEmptyCollars() }}
              </div>
            }
            <vld-message [validity]="validity('collar')" [immediate]="true"></vld-message>
          </cs-form-field>
        </div>
      </cs-item-group>
    }

    <!-- Stacked Weights Section -->
    @if (!isStep1 && equipment.type === EquipmentType.Stack) {
      <cs-item-group
        [title]="LL.equipment.sections.stackedWeights()">
        <div class="section-description">
          {{ LL.equipment.descriptions.stack() }}
        </div>
        @if (!equipment.stack || equipment.stack.length < 1) {
          <div class="stacked-empty">
            {{ LL.equipment.placeholders.noStackedWeights() }}
          </div>
        }
        @for (stack of stackedGroups; track stack; let i = $index) {
          <div class="weight-group-row e2e-item"
            [ngClass]="{'focused-row': focusedRow==i}" (touchend)="onRowFocus(i)">
            <div class="weight-inputs-row">
              <cs-form-field class="weight-field" [label]="LL.equipment.labels.from()">
                <cs-input-measure type="addedweight_kg" [(userUnit)]="equipment.weight_unit"
                  [(canonValue)]="stack.firstWeight_kg" (userUnitChange)="userUnitChanged()"
                (editDone)="someWeightChanged()" [showUnit]="false"></cs-input-measure>
              </cs-form-field>
              <cs-form-field class="weight-field" [label]="LL.equipment.labels.to()">
                <cs-input-measure type="addedweight_kg" [(userUnit)]="equipment.weight_unit"
                  [(canonValue)]="stack.lastWeight_kg" (userUnitChange)="userUnitChanged()" (editDone)="someWeightChanged()"
                [showUnit]="false"></cs-input-measure>
              </cs-form-field>
              <cs-form-field class="weight-field" [label]="LL.equipment.labels.increments()">
                <cs-input-measure type="addedweight_kg" [(userUnit)]="equipment.weight_unit"
                  [(canonValue)]="stack.stepWeight_kg" (userUnitChange)="userUnitChanged()" (editDone)="someWeightChanged()"
                [showUnit]="false"></cs-input-measure>
              </cs-form-field>
              <cs-icon name="remove-circle" class="delete-row-icon" (click)="removeStacked(stack)"></cs-icon>
            </div>
            <div class="weight-group-summary">
              {{ getStackedSummary(stack) }}
            </div>
          </div>
        }
        <div class="add-row">
          <cs-button tertiary (click)="addStacked()" [label]="LL.equipment.buttons.addStackedWeight()"
          icon="add"></cs-button>
        </div>
      </cs-item-group>
    }

    <!-- Fixed Weights (Dumbbells) Section -->
    @if (!isStep1 && equipment.type === EquipmentType.Fixed) {
      <cs-item-group
        [title]="LL.equipment.sections.dumbbellWeights()">
        @if (!equipment.fixed || equipment.fixed.length < 1) {
          <div class="fixed-empty">
            {{ LL.equipment.placeholders.noWeightsSet() }}
          </div>
        }
        @for (fixed of fixedGroups; track fixed; let i = $index) {
          <div class="weight-group-row e2e-item"
            [ngClass]="{'focused-row': focusedRow==i}" (touchend)="onRowFocus(i)">
            <div class="weight-inputs-row">
              <cs-form-field class="weight-field" [label]="LL.equipment.labels.from()">
                <cs-input-measure type="addedweight_kg" [(userUnit)]="equipment.weight_unit"
                  [(canonValue)]="fixed.firstWeight_kg" (userUnitChange)="userUnitChanged()"
                (editDone)="someWeightChanged()" [showUnit]="false"></cs-input-measure>
              </cs-form-field>
              <cs-form-field class="weight-field" [label]="LL.equipment.labels.to()">
                <cs-input-measure type="addedweight_kg" [(userUnit)]="equipment.weight_unit"
                  [(canonValue)]="fixed.lastWeight_kg" (userUnitChange)="userUnitChanged()" (editDone)="someWeightChanged()"
                [showUnit]="false"></cs-input-measure>
              </cs-form-field>
              <cs-form-field class="weight-field" [label]="LL.equipment.labels.increments()">
                <cs-input-measure type="addedweight_kg" [(userUnit)]="equipment.weight_unit"
                  [(canonValue)]="fixed.stepWeight_kg" (userUnitChange)="userUnitChanged()" (editDone)="someWeightChanged()"
                [showUnit]="false"></cs-input-measure>
              </cs-form-field>
              <cs-icon name="remove-circle" class="delete-row-icon" (click)="removeFixed(fixed)"></cs-icon>
            </div>
            <div class="weight-group-summary">
              {{ getFixedSummary(fixed) }}
            </div>
          </div>
        }
        <div class="add-row">
          <cs-button tertiary (click)="addFixed()" [label]="LL.equipment.buttons.addDumbbells()"
          icon="add"></cs-button>
        </div>
      </cs-item-group>
    }

    <!-- Plates Section (unified for all types except Manual) -->
    @if (!isStep1 && equipment.type !== EquipmentType.Manual) {
<cs-item-group [title]="(equipment.type === EquipmentType.Bar || equipment.type === EquipmentType.Plates)
        ? LL.equipment.sections.plates()
        : LL.equipment.labels.microPlatesOptional()">
        <!-- Description varies by type -->
        <div class="section-description">
          @if (equipment.type === EquipmentType.Bar) {
            {{ LL.equipment.descriptions.barPlates() }}
          }
          @if (equipment.type === EquipmentType.Plates) {
            {{ LL.equipment.descriptions.plates() }}
          }
          @if (equipment.type === EquipmentType.Stack) {
            {{ LL.equipment.stack.additionalMicroloading() }}
          }
          @if (equipment.type === EquipmentType.Fixed) {
            {{ LL.equipment.stack.additionalMicroloadingWeights() }}
          }
        </div>
        @if (plateGroups.length < 1) {
          <div class="plates-empty">
            {{ LL.equipment.labels.noPlatesYet() }}
          </div>
        }
        @for (plateGroup of plateGroups; track plateGroup; let i = $index) {
          <div class="weight-group-row e2e-item"
            [ngClass]="{'focused-row': focusedRow==i+100}" (touchend)="onRowFocus(i+100)">
            <div class="weight-inputs-row">
              <cs-form-field class="weight-field"
                [label]="plateCountLabel">
                <cs-input-measure type="number" [(canonValue)]="plateGroup.count" (editDone)="someWeightChanged()"
                [customUnitConfigs]="getPlateCountConfig()"></cs-input-measure>
              </cs-form-field>
              <cs-form-field class="weight-field" [label]="LL.equipment.labels.individualWeight()">
                <cs-input-measure type="addedweight_kg" [(userUnit)]="equipment.weight_unit"
                  [(canonValue)]="plateGroup.weight_kg" (userUnitChange)="userUnitChanged()"
                (editDone)="someWeightChanged()" (zoom)="onRowFocus(i+100)" [showUnit]="false"></cs-input-measure>
              </cs-form-field>
              <cs-icon name="remove-circle" class="delete-row-icon" (click)="removePlate(plateGroup)"></cs-icon>
            </div>
            <div class="weight-group-summary">
              {{ getPlatesSummary(plateGroup) }}
            </div>
          </div>
        }
        <div class="add-row">
          <cs-button tertiary (click)="addPlate()" [label]="LL.common.buttons.addPlates()"
          icon="add"></cs-button>
        </div>
        @if (canCleanUp()) {
          <div class="cleanup-row">
            <div class="note">{{ LL.equipment.stack.unsortedWeights() }}</div>
            <cs-button secondary (click)="cleanUp()" [label]="LL.common.buttons.cleanUp()"></cs-button>
          </div>
        }
      </cs-item-group>
    }
  </cs-item-list>
</cs-content>

<cs-page-action-buttons [showCancel]="true" (cancel)="handleCancelButton($event)">
  <cs-button medium primary [label]="LL.common.buttons.save()" [disabled]="isStep1 || !isModified"
  (click)="handleAcceptButton($event)"></cs-button>
</cs-page-action-buttons>